import { useEffect } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import useLang from "@/hooks/useLang";
import DashboardLayout from "@/UI/components/DashboardLayout";
import PermissionGate from "@/UI/components/PermissionGate";
import PopupPaper from "@/UI/components/PopupPaper";
import ChooseLangScreen from "@/UI/screens/ChooseLangScreen";
import ClientsScreen from "@/UI/screens/ClientsScreen";
import CouponsScreen from "@/UI/screens/CouponsScreen";
import HomeScreen from "@/UI/screens/HomeScreen";
import LoginScreen from "@/UI/screens/LoginScreen";
import LogsScreen from "@/UI/screens/LogsScreen";
import NotFoundScreen from "@/UI/screens/NotFoundScreen";
import OneClientScreen from "@/UI/screens/OneClientScreen";
import OneLogScreen from "@/UI/screens/OneLogScreen";
import OneStoreScreen from "@/UI/screens/OneStoreScreen";
import StoresScreen from "@/UI/screens/StoresScreen";
import SubscriptionsScreen from "@/UI/screens/SubscriptionsScreen";
import ContactsScreen from "@/UI/screens/ContactsScreen";
import SupportScreen from "@/UI/screens/SupportScreen";
import VerifyOtpScreen from "@/UI/screens/VerifyOtpScreen";

function RootLayout() {
    return (
        <>
            <Outlet />
            <PopupPaper />
        </>
    );
}

/** Wraps a section so a hidden tab cannot be reached by typing its URL. The API re-checks anyway. */
function Gated({ model, children }: { model: string; children: React.ReactNode }) {
    return <PermissionGate model={model}>{children}</PermissionGate>;
}

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { path: "/choose-lang", element: <ChooseLangScreen /> },
            { path: "/login", element: <LoginScreen /> },
            { path: "/verify-otp", element: <VerifyOtpScreen /> },
            {
                path: "/",
                element: <DashboardLayout />,
                children: [
                    { index: true, element: <HomeScreen /> },
                    {
                        path: "stores",
                        element: (
                            <Gated model="stores">
                                <StoresScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "stores/:id",
                        element: (
                            <Gated model="stores">
                                <OneStoreScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "subscriptions",
                        element: (
                            <Gated model="subscriptions">
                                <SubscriptionsScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "support",
                        element: (
                            <Gated model="support">
                                <SupportScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "contacts",
                        element: (
                            <Gated model="contacts">
                                <ContactsScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "coupons",
                        element: (
                            <Gated model="coupons">
                                <CouponsScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "clients",
                        element: (
                            <Gated model="clients">
                                <ClientsScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "clients/:id",
                        element: (
                            <Gated model="clients">
                                <OneClientScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "logs",
                        element: (
                            <Gated model="logs">
                                <LogsScreen />
                            </Gated>
                        ),
                    },
                    {
                        path: "logs/:id",
                        element: (
                            <Gated model="logs">
                                <OneLogScreen />
                            </Gated>
                        ),
                    },
                ],
            },
            { path: "*", element: <NotFoundScreen /> },
        ],
    },
]);

export default function Router() {
    const lang = useLang((state) => state.lang);

    useEffect(() => {
        document.documentElement.dir = lang === "en" ? "ltr" : "rtl";
        document.documentElement.lang = lang ?? "ar";
    }, [lang]);

    useEffect(() => {
        if (!lang && router.state.location.pathname !== "/choose-lang") router.navigate("/choose-lang");
    }, [lang]);

    return <RouterProvider router={router} />;
}
